import { ApolloError } from '@apollo/client'
import { format, parseJSON } from 'date-fns'
import { UseFormSetError } from 'react-hook-form'

import { Roles } from 'constants/EntityConstants'
import {
  DATE_FORMAT,
  DUPLICATE_EMAIL_MESSAGE,
  DUPLICATE_USER_FIELD_CODES,
  DUPLICATE_USER_FIELD_PATTERNS,
  INVALID_USERNAME_MESSAGE,
} from 'constants/index'
import { CreateUpdateUserDtoInput, GetUserDetailsQuery } from 'generated/graphql'
import { TUserForm } from 'types'

export interface ILegalDocumentAdherence {
  isApplicable: boolean
  acceptedAt?: string | null
  version?: string | null
}

type GraphQLErrorLike = {
  message?: string
  extensions?: { code?: string }
}

const INVALID_USERNAME_PATTERN = /invalid.*username|username.*invalid|invalidusername/i

const getGraphQLError = (error: ApolloError): GraphQLErrorLike | undefined => {
  const graphQLError = error.graphQLErrors?.[0]
  if (graphQLError) return graphQLError

  const networkError = error.networkError as
    | { result?: { errors?: GraphQLErrorLike[] } }
    | null
    | undefined

  return networkError?.result?.errors?.[0]
}

export const setDuplicateFieldError = (
  { code, message }: { code?: string; message?: string } = {},
  setError: UseFormSetError<any>,
): boolean => {
  if (!message) return false

  const field =
    DUPLICATE_USER_FIELD_CODES[code ?? ''] ??
    DUPLICATE_USER_FIELD_PATTERNS.find(([pattern]) => pattern.test(message))?.[1] ??
    (INVALID_USERNAME_PATTERN.test(message) ? 'username' : undefined)

  if (!field) return false

  setError(field, {
    type: 'server',
    message:
      field === 'email'
        ? DUPLICATE_EMAIL_MESSAGE
        : INVALID_USERNAME_PATTERN.test(message)
          ? INVALID_USERNAME_MESSAGE
          : message,
  })
  return true
}

export const setMutationFieldError = (
  error: ApolloError,
  setError: UseFormSetError<any>,
): boolean => setDuplicateFieldError(getGraphQLError(error), setError)

export const getMutationErrorMessage = (error: ApolloError): string =>
  getGraphQLError(error)?.message || error.message

export const formatAdherence = (adherence?: ILegalDocumentAdherence | null): string => {
  if (!adherence || !adherence.isApplicable) return 'N/A'
  if (!adherence.acceptedAt || !adherence.version) return '—'
  return `${format(parseJSON(adherence.acceptedAt), DATE_FORMAT)} (${adherence.version})`
}

export const normalizeUserForm = (
  user: NonNullable<GetUserDetailsQuery['userDetails']>,
): TUserForm => {
  const companyDetails = user.companyDetails
    ? {
        companyName: user.companyDetails.companyName,
        customerId: user.companyDetails.customerId,
        contactId: user.companyDetails.contactId,
      }
    : undefined

  return {
    username: user.userName,
    role: user.role,
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    city: user.city ?? '',
    postcode: user.postcode ?? '',
    dob: user.dob,
    countryId: user.countryId,
    phoneNumber: user.phoneNumber,
    driverId: user.driverId,
    companyDetails,
  }
}

export const normalizeUserDto = (formData: TUserForm): CreateUpdateUserDtoInput => {
  const companyDetails =
    formData.role === Roles.customer && formData.companyDetails
      ? {
          companyName: formData.companyDetails.companyName,
          customerId: formData.companyDetails.customerId,
          contactId: formData.companyDetails?.contactId
            ? Number(formData.companyDetails?.contactId)
            : undefined,
        }
      : undefined

  return {
    username: formData.username,
    password: formData.password,
    role: formData.role,
    firstName: formData.firstName,
    lastName: formData.lastName,
    email: formData.email,
    city: formData.city,
    postcode: formData.postcode,
    dob: formData.dob,
    countryId: String(formData.countryId),
    phoneNumber: formData.phoneNumber,
    companyDetails,
  }
}

export const formatOnboardingStatus = (isOnboardingCompleted?: boolean | null): string => {
  if (isOnboardingCompleted === undefined || isOnboardingCompleted === null) return 'N/A'
  return isOnboardingCompleted ? 'Yes' : 'No'
}
